import { Link } from 'react-router-dom';
import { SeverityBadge } from './SeverityBadge';
import type { Diagnosis } from '../../types/diagnosis';

interface DiagnosisCardProps {
  diagnosis: Diagnosis;
  plantId: string;
}

/**
 * Editorial diagnosis card for the Health Timeline.
 */
export function DiagnosisCard({ diagnosis, plantId }: DiagnosisCardProps) {
  const date = diagnosis.createdAt.toDate();
  const formattedDate = date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  const firstProblem = diagnosis.problems[0];
  const hasProblems = !diagnosis.isHealthy && firstProblem !== undefined;

  return (
    <Link
      to={`/plant/${plantId}/diagnosis/${diagnosis.id}`}
      className="block border border-gray-200 rounded-lg p-4 bg-white hover:border-gray-400 transition-colors min-h-[44px]"
      aria-label={`View diagnosis from ${formattedDate}`}
    >
      <div className="flex items-start gap-3">
        {/* Status indicator */}
        <div className="flex-shrink-0 mt-0.5">
          {diagnosis.isHealthy ? (
            <div
              className="w-8 h-8 rounded-full bg-status-green/10 flex items-center justify-center"
              aria-hidden="true"
            >
              <span className="material-symbols-outlined text-status-green" style={{ fontSize: 18 }}>
                check_circle
              </span>
            </div>
          ) : (
            <div
              className="w-8 h-8 rounded-full bg-editorial-gold/10 flex items-center justify-center"
              aria-hidden="true"
            >
              <span className="material-symbols-outlined text-editorial-gold" style={{ fontSize: 18 }}>
                warning
              </span>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-sans text-gray-400 uppercase tracking-wider">{formattedDate}</p>
            {hasProblems && (
              <SeverityBadge severity={firstProblem.severity} size="sm" />
            )}
          </div>

          {diagnosis.isHealthy ? (
            <>
              <p className="font-display font-bold text-status-green mt-1">Healthy checkup</p>
              <p className="text-sm font-serif text-gray-500">No issues detected</p>
            </>
          ) : hasProblems ? (
            <>
              <p className="font-display font-bold text-editorial-black mt-1">
                {diagnosis.problems.length === 1
                  ? firstProblem.problem
                  : `${diagnosis.problems.length} issues detected`}
              </p>
              {diagnosis.problems.length === 1 && (
                <p className="text-sm font-serif text-gray-500 line-clamp-1">
                  {firstProblem.cause}
                </p>
              )}
            </>
          ) : null}
        </div>

        {/* Arrow */}
        <div className="flex-shrink-0 self-center" aria-hidden="true">
          <span className="material-symbols-outlined text-gray-400" style={{ fontSize: 20 }}>
            arrow_forward
          </span>
        </div>
      </div>
    </Link>
  );
}
